<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PipelineMultiSelect from './PipelineMultiSelect.vue';
import { emptyPipelineFilters, pipelinePreset } from './pipelineFilters';

const props = defineProps({
  modelValue: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
  stages: { type: Object, default: () => ({}) },
  storageKey: { type: String, required: true },
  timezone: { type: String, default: '' },
  search: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue', 'update:search']);
const { t } = useI18n();
const draft = ref(emptyPipelineFilters());
const details = ref(null);
const saved = ref([]);
const viewName = ref('');
const selectedView = ref('');
const section = ref('COMMERCIAL');
const label = key => t(`KANBAN_VIEW.CRM.FILTERS.${key}`);
const copy = value => JSON.parse(JSON.stringify(value));
watch(
  () => props.modelValue,
  value => {
    draft.value = copy(value);
  },
  { immediate: true, deep: true }
);
watch(
  () => props.storageKey,
  key => {
    selectedView.value = '';
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '[]');
      saved.value = Array.isArray(parsed) ? parsed : [];
    } catch {
      saved.value = [];
    }
  },
  { immediate: true }
);

const fields = [
  { key: 'deal_status', options: ['all', 'open', 'won', 'lost'] },
  { key: 'date_field', options: ['created', 'closed', 'stage'] },
  { key: 'period', options: ['', 'today', 'yesterday', 'week', 'month'] },
  { key: 'date_from', type: 'date' },
  { key: 'date_to', type: 'date' },
  { key: 'value_min', type: 'number' },
  { key: 'value_max', type: 'number' },
  { key: 'source', type: 'text' },
  { key: 'campaign', type: 'text' },
  { key: 'product', type: 'text' },
  { key: 'loss_reason', type: 'text' },
  { key: 'inactive_days', type: 'number' },
  { key: 'stage_days', type: 'number' },
  { key: 'activity', options: ['', 'overdue', 'no_next'] },
];
const sectionFields = {
  COMMERCIAL: [
    'value_min',
    'value_max',
    'source',
    'campaign',
    'product',
    'loss_reason',
  ],
  FOLLOW_UP: ['inactive_days', 'stage_days', 'activity'],
};
const visibleFields = panel => {
  const panels = {
    DATE_BUTTON: ['date_field', 'period', 'date_from', 'date_to'],
    STATUS_BUTTON: ['deal_status'],
    MORE_FILTERS: sectionFields[section.value],
  };
  const keys = panels[panel];
  return fields.filter(
    field =>
      keys.includes(field.key) &&
      (!draft.value.period || !['date_from', 'date_to'].includes(field.key))
  );
};
const ownerOptions = computed(() => [
  { id: 'me', name: label('ME') },
  { id: 'unassigned', name: label('UNASSIGNED') },
  ...props.agents.map(agent => ({ id: String(agent.id), name: agent.name })),
]);
const stageOptions = computed(() =>
  Object.entries(props.stages).map(([id, stage]) => ({
    id,
    name: stage.label || id,
  }))
);
const updateSelection = (key, values) =>
  emit('update:modelValue', { ...props.modelValue, [key]: values });
const activeCount = computed(() => {
  const defaults = emptyPipelineFilters();
  return Object.keys(defaults).filter(
    key =>
      JSON.stringify(props.modelValue[key]) !== JSON.stringify(defaults[key])
  ).length;
});
const activeSummary = computed(() => {
  if (!activeCount.value) return '';
  const filters = props.modelValue;
  return [
    filters.deal_status !== 'all' ? label(filters.deal_status) : '',
    filters.period || filters.date_from || filters.date_to
      ? label(filters.date_field)
      : '',
    filters.period
      ? label(filters.period)
      : [filters.date_from, filters.date_to].filter(Boolean).join(' — '),
  ]
    .filter(Boolean)
    .join(' · ');
});
const apply = value => {
  emit('update:modelValue', copy(value));
  details.value?.forEach(element => element.removeAttribute('open'));
};
const preset = key => {
  selectedView.value = '';
  emit('update:search', '');
  apply(pipelinePreset(key));
};
const clear = () => {
  selectedView.value = '';
  emit('update:search', '');
  apply(emptyPipelineFilters());
};
const persist = views => {
  try {
    localStorage.setItem(props.storageKey, JSON.stringify(views));
    saved.value = views;
    return true;
  } catch {
    useAlert(label('SAVE_ERROR'));
    return false;
  }
};
const saveView = () => {
  const name = viewName.value.trim().slice(0, 80);
  if (!name) return;
  const views = saved.value.filter(view => view.name !== name);
  if (views.length >= 20) {
    useAlert(label('SAVE_LIMIT'));
    return;
  }
  if (
    persist([
      ...views,
      { name, filters: copy(props.modelValue), search: props.search },
    ])
  ) {
    selectedView.value = name;
    viewName.value = '';
  }
};
const loadView = () => {
  const view = saved.value.find(item => item.name === selectedView.value);
  if (!view) return;
  emit('update:search', view.search || '');
  apply({ ...emptyPipelineFilters(), ...view.filters });
};
const removeView = () => {
  if (persist(saved.value.filter(item => item.name !== selectedView.value)))
    selectedView.value = '';
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <button
      v-for="key in ['won', 'lost', 'new', 'mine']"
      :key="key"
      type="button"
      :data-testid="`crm-preset-${key}`"
      class="rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-1"
      @click="preset(key)"
    >
      {{ label(`PRESET_${key}`) }}
    </button>
    <span v-if="activeSummary" class="text-xs text-n-slate-11">{{
      activeSummary
    }}</span>
    <PipelineMultiSelect
      :model-value="modelValue.assignee_ids"
      :options="ownerOptions"
      :title="label('OWNERS_BUTTON')"
      @update:model-value="updateSelection('assignee_ids', $event)"
    />
    <PipelineMultiSelect
      :model-value="modelValue.stage_names"
      :options="stageOptions"
      :title="label('STAGES_BUTTON')"
      @update:model-value="updateSelection('stage_names', $event)"
    />
    <details
      v-for="panel in ['DATE_BUTTON', 'STATUS_BUTTON', 'MORE_FILTERS']"
      :key="panel"
      ref="details"
      name="crm-pipeline-menu"
      class="relative"
    >
      <summary
        class="cursor-pointer rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-12"
      >
        {{ label(panel) }}
      </summary>
      <form
        class="absolute left-0 top-10 z-50 grid max-h-[70vh] w-[min(85vw,32rem)] grid-cols-2 gap-3 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        @submit.prevent="apply(draft)"
      >
        <div
          v-if="panel === 'MORE_FILTERS'"
          class="col-span-2 flex gap-1 border-b border-n-weak pb-3"
        >
          <button
            v-for="key in Object.keys(sectionFields)"
            :key="key"
            type="button"
            class="rounded-lg px-3 py-2 text-xs"
            :class="
              section === key
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="section = key"
          >
            {{ label(key) }}
          </button>
        </div>
        <p
          v-if="panel === 'DATE_BUTTON'"
          class="col-span-2 text-xs text-n-slate-10"
        >
          {{ label('HELP') }} {{ timezone }}
        </p>
        <label
          v-for="field in visibleFields(panel)"
          :key="field.key"
          class="text-xs text-n-slate-11"
        >
          {{ label(field.key) }}
          <select
            v-if="field.options"
            v-model="draft[field.key]"
            class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2"
          >
            <option
              v-for="option in field.options"
              :key="option"
              :value="option"
            >
              {{
                label(
                  option || (field.key === 'period' ? 'CUSTOM_PERIOD' : 'ANY')
                )
              }}
            </option>
          </select>
          <input
            v-else
            v-model="draft[field.key]"
            :type="field.type"
            min="0"
            :step="field.key.startsWith('value_') ? '0.01' : '1'"
            class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2"
          />
        </label>
        <label
          v-if="panel === 'MORE_FILTERS' && section === 'COMMERCIAL'"
          class="col-span-2 text-xs text-n-slate-11"
        >
          {{ label('closed_by_id') }}
          <select
            v-model="draft.closed_by_id"
            class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2"
          >
            <option value="">{{ label('ANY') }}</option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>
        <p
          v-if="panel === 'MORE_FILTERS' && section === 'FOLLOW_UP'"
          class="col-span-2 text-xs text-n-slate-10"
        >
          {{ label('FOLLOW_UP_HELP') }}
        </p>
        <button
          type="submit"
          class="col-span-2 rounded-lg bg-n-brand px-3 py-2 text-sm text-white"
        >
          {{ label('APPLY') }}
        </button>
      </form>
    </details>
    <button
      type="button"
      class="px-2 py-2 text-xs text-n-slate-11"
      @click="clear"
    >
      {{ label('CLEAR') }}
    </button>
    <details name="crm-pipeline-menu" class="relative">
      <summary
        class="cursor-pointer rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-12"
      >
        {{ label('VIEWS') }}
      </summary>
      <div
        class="absolute left-0 top-10 z-50 w-72 space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
      >
        <p class="text-xs text-n-slate-10">{{ label('VIEWS_HELP') }}</p>
        <select
          v-model="selectedView"
          :aria-label="label('VIEWS')"
          class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm"
          @change="loadView"
        >
          <option value="">{{ label('SELECT_VIEW') }}</option>
          <option v-for="view in saved" :key="view.name" :value="view.name">
            {{ view.name }}
          </option>
        </select>
        <button
          v-if="selectedView"
          type="button"
          class="text-xs text-n-ruby-9"
          @click="removeView"
        >
          {{ label('REMOVE_VIEW') }}
        </button>
        <form class="space-y-2" @submit.prevent="saveView">
          <input
            v-model="viewName"
            maxlength="80"
            required
            :placeholder="label('VIEW_NAME')"
            :aria-label="label('VIEW_NAME')"
            class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm"
          />
          <button
            type="submit"
            class="rounded-lg bg-n-brand px-3 py-2 text-xs text-white"
          >
            {{ label('SAVE_VIEW') }}
          </button>
        </form>
      </div>
    </details>
  </div>
</template>
